/**
 * `AccountApiError` is a real `instanceof ApiError` subclass so callers in
 * another bundle can still test for it (see `@dealr/api-service/src/errors.ts`).
 */

import { ApiError as TransportApiError, toApiError as toTransportApiError } from '@dealr/transport';
import type { ApiErrorKind, ApiErrorRaw } from '@dealr/transport';

export type AccountApiErrorKind = ApiErrorKind;

export class AccountApiError extends TransportApiError<AccountApiErrorKind> {
  constructor(kind: AccountApiErrorKind, status: number, serverMessage?: string, raw?: ApiErrorRaw) {
    super(kind, status, serverMessage, raw);
    this.name = 'AccountApiError';
  }
}

/** Normalizes any transport rejection into an `AccountApiError`. Never throws. */
export function toAccountApiError(rejection: unknown): AccountApiError {
  if (rejection instanceof AccountApiError) return rejection;
  const neutral = toTransportApiError(rejection);
  return new AccountApiError(neutral.kind, neutral.status, neutral.serverMessage, {
    data: neutral.data,
    statusText: neutral.statusText,
  });
}

/**
 * The message to show for a rejection. Account-security errors are
 * `{success:false, error:"..."}`; older endpoints answer with a bare string.
 */
export function accountErrorMessage(rejection: unknown, fallback: string): string {
  const error = toAccountApiError(rejection);
  const data = error.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const body = data as Record<string, unknown>;
    if (typeof body['error'] === 'string' && body['error']) return body['error'];
    if (typeof body['message'] === 'string' && body['message']) return body['message'];
  }
  if (error.kind === 'network') return 'Check your connection and try again.';
  return fallback;
}

/** The `code` an account-security error carries next to `error` (e.g. `two_person_rule`), or null. */
export function accountErrorCode(rejection: unknown): string | null {
  const data = toAccountApiError(rejection).data;
  if (data && typeof data === 'object') {
    const code = (data as Record<string, unknown>)['code'];
    if (typeof code === 'string' && code) return code;
  }
  return null;
}
